import { useEffect, useState } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Activity, AlertTriangle, BrainCircuit, Database, Gauge, Image, RefreshCw, ShieldCheck, TrendingUp, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type {
  RagQualityRange,
  RagQualityResponse,
  RagQualityProviderLatency,
  RagQualityBenchmarkRun,
} from '@easyflow/shared';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

interface Tenant { id: string; name: string; slug?: string }

const RANGES: readonly RagQualityRange[] = ['24h', '7d', '30d'] as const;

function fmtMs(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v)}ms`;
}

function fmtBytes(v: number): string {
  if (v <= 0) return '0 B';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const PROVIDER_PRETTY: Record<string, string> = {
  gemini: 'Gemini',
  google: 'Gemini',
  openai: 'OpenAI',
  claude: 'Claude',
  cohere: 'Cohere',
  voyage: 'Voyage',
};

function prettyProvider(id: string): string {
  return PROVIDER_PRETTY[id.toLowerCase()] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

function providerLabel(p: RagQualityProviderLatency, fallback: string): string {
  if (!p.id && !p.model) return fallback;
  const name = p.id ? prettyProvider(p.id) : fallback;
  return p.model ? `${name} · ${p.model}` : name;
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

const SKIPPED_PRETTY: Record<string, string> = {
  no_candidates: 'Nessun candidato',
  below_topn: 'Candidati ≤ topN',
  budget_exceeded: 'Budget superato',
  no_provider: 'Nessun provider',
  all_providers_failed: 'Tutti i provider falliti',
};

const HYBRID_PRETTY: Record<string, string> = {
  text_only: 'Solo testo',
  multimodal_native: 'Multimodale nativo',
  hybrid_bypass: 'Bypass ibrido',
  multimodal_skipped: 'Multimodale saltato',
};

const MERGE_PRETTY: Record<string, string> = {
  rrf: 'RRF (k=60)',
  legacy_sum: 'Somma legacy',
  single: 'Single provider',
  none: 'Nessun merge',
};

function breakerBadgeVariant(state: string): 'default' | 'destructive' | 'outline' {
  if (state === 'open') return 'destructive';
  if (state === 'half-open') return 'outline';
  return 'default';
}

export default function RAGQualityMaster() {
  const { toast } = useToast();
  const [range, setRange] = useState<RagQualityRange>('24h');
  const [tenantId, setTenantId] = useState<string>('');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [data, setData] = useState<RagQualityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiClient.get<{ tenants?: Tenant[] }>('/master/tenants').then((res) => {
      setTenants(res.data?.tenants ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy guardrail #1219
  }, [range, tenantId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const qs = new URLSearchParams({ range });
      if (tenantId) qs.set('tenantId', tenantId);
      const res = await apiClient.get<RagQualityResponse>(`/master/rag/quality?${qs.toString()}`);
      setData(res.success && res.data ? res.data : null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Caricamento dati fallito';
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-6 p-6">
        <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={6} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-muted-foreground dark:text-muted-foreground/70">Nessun dato disponibile.</div>
    );
  }

  const anyAlert = data.alert_flags.p95_high || data.alert_flags.agreement_low || data.alert_flags.budget_exceeded_high;
  const skippedRows = data.skipped_reasons.map((r) => ({
    reason: SKIPPED_PRETTY[r.reason] ?? r.reason,
    count: r.count,
  }));
  const hybridRows = data.multimodal.hybrid_strategies.map((r) => ({
    strategy: HYBRID_PRETTY[r.strategy] ?? r.strategy,
    count: r.count,
  }));
  const breakerEntries = data.breaker ? Object.entries(data.breaker.state) : [];

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-3">
              <BrainCircuit className="h-8 w-8" />
              RAG Quality
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
              Telemetria qualità retrieval, recall, latenze e benchmark — finestra {range}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border border-border dark:border-white/10 overflow-hidden">
              {RANGES.map(r => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    range === r
                      ? 'bg-purple-500 text-white'
                      : 'bg-white dark:bg-slate-900 text-muted-foreground dark:text-muted-foreground/60 hover:bg-muted'
                  }`}
                >{r}</button>
              ))}
            </div>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-border dark:border-white/10 bg-white dark:bg-slate-900 text-sm text-foreground dark:text-foreground"
            >
              <option value="">Tutti i tenant</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        {/* Alerts banner */}
        {anyAlert && (
          <GlassCard className="border-l-4 border-l-red-500">
            <GlassCardHeader>
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                <div className="flex-1">
                  <GlassCardTitle className="text-lg">Allarmi attivi</GlassCardTitle>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.alert_flags.p95_high && (
                      <Badge variant="destructive">p95 fused {fmtMs(data.latency.fused.p95)} &gt; soglia {data.thresholds.p95_ms}ms</Badge>
                    )}
                    {data.alert_flags.agreement_low && (
                      <Badge variant="destructive">agreement {data.agreement.avg.toFixed(2)} &lt; soglia {data.thresholds.agreement_min}</Badge>
                    )}
                    {data.alert_flags.budget_exceeded_high && (
                      <Badge variant="destructive">budget superato {data.totals.budget_exceeded_pct.toFixed(1)}% &gt; soglia {data.thresholds.budget_exceeded_pct}%</Badge>
                    )}
                  </div>
                </div>
              </div>
            </GlassCardHeader>
          </GlassCard>
        )}

        {/* Scorecards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <GlassCard className="border-l-4 border-l-blue-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2"><Activity className="h-4 w-4" /> Rerank Calls</GlassCardDescription>
              <GlassCardTitle className="text-3xl">{data.totals.rerank_calls.toLocaleString()}</GlassCardTitle>
              <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                {data.totals.executed_calls.toLocaleString()} eseguite · {data.totals.early_exit_count.toLocaleString()} early-exit · {data.totals.rerank_cache_hits.toLocaleString()} cache
              </div>
            </GlassCardHeader>
          </GlassCard>
          <GlassCard className={`border-l-4 ${data.alert_flags.p95_high ? 'border-l-red-500' : 'border-l-green-500'}`}>
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2"><Gauge className="h-4 w-4" /> Latency p95 (fused)</GlassCardDescription>
              <GlassCardTitle className="text-3xl">{fmtMs(data.latency.fused.p95)}</GlassCardTitle>
              <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">p50 {fmtMs(data.latency.fused.p50)} · p99 {fmtMs(data.latency.fused.p99)} — solo rerank eseguiti</div>
            </GlassCardHeader>
          </GlassCard>
          <GlassCard className={`border-l-4 ${data.alert_flags.agreement_low ? 'border-l-red-500' : 'border-l-purple-500'}`}>
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Agreement Score</GlassCardDescription>
              <GlassCardTitle className="text-3xl">{data.agreement.avg.toFixed(2)}</GlassCardTitle>
              <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Soglia minima {data.thresholds.agreement_min}</div>
            </GlassCardHeader>
          </GlassCard>
          <GlassCard className="border-l-4 border-l-orange-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2"><Database className="h-4 w-4" /> Cache Hit-Rate</GlassCardDescription>
              <GlassCardTitle className="text-3xl">{fmtPct(data.totals.cache_hit_rate)}</GlassCardTitle>
              <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">{data.totals.cache_hits.toLocaleString()} / {data.totals.cache_lookups.toLocaleString()} lookup</div>
            </GlassCardHeader>
          </GlassCard>
        </div>

        {/* Latency series + agreement histogram */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Latenza fused (p50 / p95)</GlassCardTitle>
              <GlassCardDescription>Serie temporale — solo rerank eseguiti</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={data.latency_series.map(s => ({ ...s, bucket: new Date(s.bucket).toLocaleString() }))}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="p50" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="p95" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Distribuzione Agreement Score</GlassCardTitle>
              <GlassCardDescription>10 bucket da 0.0 a 1.0</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.agreement.histogram.map(b => ({ bucket: b.bucket.toFixed(1), count: b.count }))}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#a855f7" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Mode distribution + fallback reasons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Mode Effective</GlassCardTitle>
              <GlassCardDescription>Distribuzione modalità di rerank</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.mode_distribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="mode" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Top Fallback Reasons</GlassCardTitle>
              <GlassCardDescription>Cause di degradazione</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.fallback_reasons} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="reason" type="category" tick={{ fontSize: 10 }} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f97316" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Skipped reasons + merge strategy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Early-exit &amp; Skip</GlassCardTitle>
              <GlassCardDescription>Chiamate uscite senza invocare i provider (skipped_reason)</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {skippedRows.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                  Nessun early-exit o skip nel periodo.
                </div>
              ) : (
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={skippedRows} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="reason" type="category" tick={{ fontSize: 10 }} width={150} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#eab308" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Merge Strategy</GlassCardTitle>
              <GlassCardDescription>Strategia di fusione usata dai rerank eseguiti</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {data.merge_strategies.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                  Nessun rerank eseguito nel periodo.
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  {data.merge_strategies.map((m) => {
                    const total = data.merge_strategies.reduce((acc, x) => acc + x.count, 0);
                    const pct = total > 0 ? (m.count / total) * 100 : 0;
                    return (
                      <div key={m.strategy}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">{MERGE_PRETTY[m.strategy] ?? m.strategy}</span>
                          <span className="text-muted-foreground dark:text-muted-foreground/70">{m.count.toLocaleString()} · {pct.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 rounded bg-muted dark:bg-white/10 overflow-hidden">
                          <div className="h-full bg-purple-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Multimodal + circuit breaker */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" /> Rerank multimodale</GlassCardTitle>
              <GlassCardDescription>Candidati visivi, bytes inviati e strategia ibrida</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-2xl font-bold">{data.multimodal.candidates_total.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Candidati multimodali</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{fmtBytes(data.multimodal.binaries_bytes_total)}</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Bytes binari inviati</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{fmtMs(data.multimodal.avg_text_ms)}</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Latenza media testo</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{fmtMs(data.multimodal.avg_multimodal_ms)}</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Latenza media multimodale</div>
                </div>
              </div>
              {hybridRows.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {hybridRows.map((h) => (
                    <Badge key={h.strategy} variant="outline">{h.strategy}: {h.count.toLocaleString()}</Badge>
                  ))}
                </div>
              )}
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Circuit Breaker</GlassCardTitle>
              <GlassCardDescription>Ultimo stato per provider osservato nella finestra</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {breakerEntries.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                  Nessuno snapshot breaker nella finestra selezionata.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {breakerEntries.map(([provider, state]) => (
                      <Badge key={provider} variant={breakerBadgeVariant(state)}>
                        {prettyProvider(provider)}: {state}
                      </Badge>
                    ))}
                  </div>
                  {data.breaker && (
                    <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                      Osservato: {new Date(data.breaker.observed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Latency breakdown + cache by backend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> Latenza per provider</GlassCardTitle>
              <GlassCardDescription>Percentili sui soli rerank eseguiti</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">p50</TableHead>
                    <TableHead className="text-right">p95</TableHead>
                    <TableHead className="text-right">p99</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {([
                    { key: 'provider_a', label: providerLabel(data.latency.provider_a, 'Provider A (primario)'), lat: data.latency.provider_a },
                    { key: 'provider_b', label: providerLabel(data.latency.provider_b, 'Provider B (secondario)'), lat: data.latency.provider_b },
                    { key: 'fused', label: 'Fuso', lat: data.latency.fused },
                  ] as const).map(row => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell className="text-right">{fmtMs(row.lat.p50)}</TableCell>
                      <TableCell className="text-right">{fmtMs(row.lat.p95)}</TableCell>
                      <TableCell className="text-right">{fmtMs(row.lat.p99)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> Cache per backend</GlassCardTitle>
              <GlassCardDescription>Hit-rate della cache semantica RAG per backend</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {data.cache_by_backend.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                  Nessun lookup cache nel periodo.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Backend</TableHead>
                      <TableHead className="text-right">Lookup</TableHead>
                      <TableHead className="text-right">Hit</TableHead>
                      <TableHead className="text-right">Hit-rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.cache_by_backend.map((c) => (
                      <TableRow key={c.backend}>
                        <TableCell className="font-medium">{c.backend}</TableCell>
                        <TableCell className="text-right">{c.total.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{c.hits.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{fmtPct(c.hit_rate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Benchmark latest per dataset+mode */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle className="text-base">Benchmark offline (ultimi per dataset/mode)</GlassCardTitle>
            <GlassCardDescription>Snapshot più recente di NDCG@5 / MRR per ciascun dataset e modalità</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            {data.benchmark_latest.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                Nessun snapshot disponibile.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dataset</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Ultimo run</TableHead>
                      <TableHead className="text-right">NDCG@5</TableHead>
                      <TableHead className="text-right">MRR</TableHead>
                      <TableHead className="text-right">Agreement</TableHead>
                      <TableHead className="text-right">p50</TableHead>
                      <TableHead className="text-right">p95</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.benchmark_latest.map((r: RagQualityBenchmarkRun) => (
                      <TableRow key={`${r.dataset}-${r.mode}-${r.id}`}>
                        <TableCell className="text-xs font-medium">{r.dataset}</TableCell>
                        <TableCell><Badge variant="outline">{r.mode}</Badge></TableCell>
                        <TableCell className="text-xs">{new Date(r.ts).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{r.ndcg5 !== null ? r.ndcg5.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right">{r.mrr !== null ? r.mrr.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right">{r.agreement_avg !== null ? r.agreement_avg.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right">{r.p50_ms !== null ? fmtMs(r.p50_ms) : '—'}</TableCell>
                        <TableCell className="text-right">{r.p95_ms !== null ? fmtMs(r.p95_ms) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>

        {/* Benchmark runs history */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle className="text-base">Benchmark offline (storico)</GlassCardTitle>
            <GlassCardDescription>NDCG@5 / MRR / latenze dai run del benchmark settimanale</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            {data.benchmark_runs.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground/70">
                Nessun run benchmark registrato. Abilita il cron via <code className="px-1 py-0.5 rounded bg-muted">RAG_BENCHMARK_CRON_ENABLED=1</code>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Dataset</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead className="text-right">NDCG@5</TableHead>
                      <TableHead className="text-right">MRR</TableHead>
                      <TableHead className="text-right">p50</TableHead>
                      <TableHead className="text-right">p95</TableHead>
                      <TableHead className="text-right">Failures</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.benchmark_runs.map((r: RagQualityBenchmarkRun) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{new Date(r.ts).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{r.dataset}</TableCell>
                        <TableCell><Badge variant="outline">{r.mode}</Badge></TableCell>
                        <TableCell className="text-right">{r.ndcg5 !== null ? r.ndcg5.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right">{r.mrr !== null ? r.mrr.toFixed(3) : '—'}</TableCell>
                        <TableCell className="text-right">{r.p50_ms !== null ? fmtMs(r.p50_ms) : '—'}</TableCell>
                        <TableCell className="text-right">{r.p95_ms !== null ? fmtMs(r.p95_ms) : '—'}</TableCell>
                        <TableCell className="text-right">{r.failures ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
